import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Eye, BarChart3, Users, CheckCircle2, MessageCircle, Phone,
  Target, Zap, Ban, Star, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEO from "@/components/SEO";
import LeadForm from "@/components/LeadForm";
import WhatsAppButton from "@/components/layout/WhatsAppButton";
import CookieConsent from "@/components/layout/CookieConsent";
import { Reveal } from "@/components/motion";
import { useSettings } from "@/context/SettingsContext";
import { waLink, trackEvent } from "@/lib/whatsapp";

const scrollToForm = () => {
  const el = document.getElementById("lp-form");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
};

export default function LandingPage() {
  const { settings } = useSettings();
  const brand = settings?.brand?.name || "NewStarAds";
  const contact = settings?.contact || {};
  const wa = settings?.whatsapp || {};

  const schema = {
    "@context": "https://schema.org", "@type": "Service",
    name: "YouTube Promotion & Ads Management", provider: { "@type": "Organization", name: brand },
    areaServed: "Worldwide", description: "Professional, policy-compliant YouTube promotion and ads management for creators and businesses.",
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO
        title="YouTube Promotion That Reaches Real Viewers | NewStarAds"
        description="Get a free YouTube campaign consultation from NewStarAds. Professional, policy-compliant video promotion and ads management for creators and businesses. No bots, no fake views."
        schema={schema}
      />

      {/* Minimal header — no nav to keep the page focused for paid traffic */}
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-lg tracking-tight">
            <span className="grid place-items-center w-8 h-8 rounded-lg bg-primary text-white font-bold">N</span>{brand}
          </Link>
          <div className="flex items-center gap-2">
            {contact.phone && (
              <a href={`tel:${contact.phone.replace(/\s/g, "")}`} className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-primary px-3 py-2">
                <Phone className="w-4 h-4" /> {contact.phone}
              </a>
            )}
            <a href={waLink(wa.number, wa.default_message)} target="_blank" rel="noreferrer" onClick={() => trackEvent("whatsapp_click", { location: "lp_header" })}>
              <Button size="sm" className="rounded-full bg-green-600 hover:bg-green-700" data-testid="lp-header-whatsapp"><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</Button>
            </a>
          </div>
        </div>
      </header>

      <main className="flex-1 pb-24 lg:pb-0">
        {/* HERO + FORM */}
        <section className="relative overflow-hidden hero-grid">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 pb-16 grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold text-slate-600">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Free consultation · No bots · Policy-compliant
              </motion.div>
              <h1 className="mt-6 font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.98]">
                Reach More Real Viewers On YouTube
              </h1>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-xl">
                Professional, transparent YouTube video promotion and ads management for creators and businesses. We help the right people discover your content — no fake views, no guarantees, just honest work.
              </p>
              <ul className="mt-7 space-y-3">
                {[
                  "Free, no-obligation campaign consultation",
                  "Compliant promotion through YouTube's own ad surfaces",
                  "Precise audience targeting by interest, country & intent",
                  "Transparent reporting from real, connected data",
                ].map((x) => (
                  <li key={x} className="flex items-start gap-2.5 text-slate-700"><CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" /> {x}</li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button size="lg" className="rounded-full px-7" onClick={scrollToForm} data-testid="lp-hero-cta">Get a Free Consultation <ArrowRight className="w-4 h-4 ml-1" /></Button>
                <a href={waLink(wa.number, wa.default_message)} target="_blank" rel="noreferrer" onClick={() => trackEvent("whatsapp_click", { location: "lp_hero" })}>
                  <Button size="lg" variant="outline" className="rounded-full px-7 border-green-500 text-green-600 hover:bg-green-50 w-full"><MessageCircle className="w-4 h-4 mr-1" /> Chat on WhatsApp</Button>
                </a>
              </div>
              <p className="mt-6 text-sm text-slate-500 flex items-start gap-2 max-w-md">
                <ShieldCheck className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                Results vary by content, niche and budget. We never promise guaranteed views, subscribers or viral results.
              </p>
            </div>

            {/* Lead form card */}
            <div id="lp-form" className="lg:sticky lg:top-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-7 shadow-xl shadow-slate-200/50">
                <h2 className="font-display text-2xl font-bold tracking-tight">Request your free consultation</h2>
                <p className="mt-1 text-sm text-slate-500">Tell us about your channel — we'll reply with honest advice on WhatsApp or email.</p>
                <div className="mt-5"><LeadForm source="google-ads" /></div>
              </div>
            </div>
          </div>
        </section>

        {/* TRUST STRIP */}
        <section className="border-y border-slate-200 bg-slate-50">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: ShieldCheck, t: "Policy-compliant", d: "Follows YouTube rules" },
              { icon: Eye, t: "Real audiences", d: "No bots or fake traffic" },
              { icon: BarChart3, t: "Honest reporting", d: "Only real, connected data" },
              { icon: Users, t: "Creator-focused", d: "Built for genuine growth" },
            ].map((x, i) => (
              <Reveal key={i} delay={i * 0.05} className="flex items-start gap-3">
                <x.icon className="w-5 h-5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
                <div><p className="font-semibold text-sm">{x.t}</p><p className="text-xs text-slate-500">{x.d}</p></div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* WHY US */}
        <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
          <Reveal className="max-w-2xl"><h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Why creators & brands choose {brand}</h2></Reveal>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Target, t: "Precise targeting", d: "We reach viewers genuinely likely to care — by interest, keyword, country and intent." },
              { icon: Zap, t: "Fast, managed setup", d: "We handle campaign structure, creative testing and optimisation end-to-end." },
              { icon: Ban, t: "Zero fake engagement", d: "We never sell bots, fake views or subscribers. It's against YouTube policy and hurts channels." },
              { icon: BarChart3, t: "Verified reporting", d: "You see real numbers from your connected analytics — nothing fabricated." },
            ].map((x, i) => (
              <Reveal key={i} delay={(i % 4) * 0.05} className="rounded-2xl border border-slate-200 p-6">
                <div className="w-11 h-11 grid place-items-center rounded-xl bg-primary/10 text-primary mb-4"><x.icon className="w-6 h-6" strokeWidth={1.5} /></div>
                <h3 className="font-display text-lg font-bold">{x.t}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{x.d}</p>
              </Reveal>
            ))}
          </div>
        </section>

        {/* PROCESS */}
        <section className="bg-slate-950 text-white">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
            <Reveal><h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">How it works</h2></Reveal>
            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {[
                { n: "01", t: "Free consultation", d: "Share your channel & goals. We give honest, realistic advice — no pressure." },
                { n: "02", t: "Strategy & launch", d: "We build targeting and launch on compliant YouTube ad surfaces." },
                { n: "03", t: "Optimise & report", d: "We refine the campaign and share real, transparent performance data." },
              ].map((x, i) => (
                <Reveal key={i} delay={i * 0.06} className="rounded-2xl border border-white/10 bg-white/5 p-6">
                  <span className="font-display font-black text-3xl text-white/10">{x.n}</span>
                  <h3 className="mt-3 font-display text-lg font-bold">{x.t}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{x.d}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-5 sm:px-8 py-20">
          <Reveal><h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Common questions</h2></Reveal>
          <Reveal delay={0.08} className="mt-8">
            <Accordion type="single" collapsible className="rounded-2xl border border-slate-200 px-5">
              {[
                { q: "Do you guarantee views or subscribers?", a: "No. We never guarantee views, subscribers, rankings or viral results. We run compliant promotion; outcomes vary by content, niche and budget." },
                { q: "Do you use bots or fake engagement?", a: "Never. We only use real, policy-compliant methods. Fake engagement violates YouTube's rules and can harm your channel." },
                { q: "How quickly can we start?", a: "After a short free consultation we can usually plan and launch within a few days, depending on your goals and assets." },
                { q: "Who owns my channel and ad account?", a: "You always retain full ownership and access. We manage everything transparently on your behalf." },
              ].map((f, i) => (
                <AccordionItem key={i} value={`f${i}`}><AccordionTrigger className="text-left font-medium py-5">{f.q}</AccordionTrigger><AccordionContent className="text-slate-600 leading-relaxed pb-5">{f.a}</AccordionContent></AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </section>

        {/* FINAL CTA */}
        <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-20">
          <Reveal className="rounded-3xl bg-primary text-white p-10 sm:p-14 text-center">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Ready to grow your YouTube reach?</h2>
            <p className="mt-3 text-white/85 max-w-xl mx-auto">Get a free, no-obligation consultation. Honest advice, transparent pricing, real results.</p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" variant="secondary" className="rounded-full px-8" onClick={scrollToForm} data-testid="lp-final-cta">Get a Free Consultation</Button>
              <a href={waLink(wa.number, wa.default_message)} target="_blank" rel="noreferrer" onClick={() => trackEvent("whatsapp_click", { location: "lp_final" })}>
                <Button size="lg" className="rounded-full px-8 bg-green-600 hover:bg-green-700 w-full"><MessageCircle className="w-4 h-4 mr-1" /> Chat on WhatsApp</Button>
              </a>
            </div>
          </Reveal>
        </section>
      </main>

      {/* Slim footer with the info Google Ads requires (privacy, contact, business) */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <p>© {new Date().getFullYear()} {brand}. Results vary — no guaranteed views or subscribers.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            {contact.phone && <a href={`tel:${contact.phone.replace(/\s/g, "")}`} className="hover:text-primary">{contact.phone}</a>}
            <Link to="/privacy-policy" className="hover:text-primary">Privacy</Link>
            <Link to="/terms" className="hover:text-primary">Terms</Link>
            <Link to="/disclaimer" className="hover:text-primary">Disclaimer</Link>
            <Link to="/contact" className="hover:text-primary">Contact</Link>
          </div>
        </div>
      </footer>

      {/* Mobile sticky CTA */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/90 backdrop-blur-xl border-t border-slate-200 px-4 py-3 flex gap-3">
        <button onClick={scrollToForm} className="flex-1 h-11 rounded-full bg-primary text-white font-medium text-sm" data-testid="lp-mobile-cta">Get Free Consultation</button>
        <a href={waLink(wa.number, wa.default_message)} target="_blank" rel="noreferrer" className="shrink-0"><button className="h-11 w-11 grid place-items-center rounded-full bg-green-600 text-white"><MessageCircle className="w-5 h-5" /></button></a>
      </div>

      <WhatsAppButton />
      <CookieConsent />
    </div>
  );
}
